import { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  FormHelperText,
  Switch,
  FormControlLabel,
  Stack,
  Snackbar,
} from "@mui/material";
import {
  useGetNewsQuery,
  useCreateNewsMutation,
  useUpdateNewsMutation,
  useDeleteNewsMutation,
  usePublishNewsMutation,
  useUploadNewsImageMutation,
  useUploadNewsVideoMutation,
  useDeleteNewsImageMutation,
  useDeleteNewsVideoMutation,
  useGetNewsCategoriesQuery,
  useGetNewsCommentsQuery,
  useDeleteNewsCommentMutation,
  useSendNewsPreviewEmailMutation,
  useSendNewsPreviewPushMutation,
  News,
  type NewsComment,
} from "../../../entities/admin-news";
import { useAppSelector } from "../../../app/store";
import { selectMe } from "../../../entities/me";
import { format } from "date-fns";
import { FileUploadInfo } from "../../../shared/ui/file-upload-info";
import {
  FILE_SIZE_LIMIT_BYTES,
  getFileSizeLimitError,
} from "../../../shared/constants/file-upload-limits";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import PhotoIcon from "@mui/icons-material/Photo";
import VideocamIcon from "@mui/icons-material/Videocam";
import PublishIcon from "@mui/icons-material/Publish";
import CommentIcon from "@mui/icons-material/Comment";
import EmailIcon from "@mui/icons-material/Email";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`news-tabpanel-${index}`}
      aria-labelledby={`news-tab-${index}`}
      sx={{ mt: 2 }}
    >
      {value === index && <Box>{children}</Box>}
    </Box>
  );
}

export const NewsManagement = () => {
  const [selectedNews, setSelectedNews] = useState<News | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const imageFileRef = useRef<HTMLInputElement>(null);
  const videoFileRef = useRef<HTMLInputElement>(null);

  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [pageToken, setPageToken] = useState<string | undefined>(undefined);
  const [pageSize] = useState(10);

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // Form states
  const [newsTitle, setNewsTitle] = useState("");
  const [newsText, setNewsText] = useState("");
  const [newsVideoDuration, setNewsVideoDuration] = useState<number>(0);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);

  // Queries and mutations
  useEffect(() => {
    setPageToken(undefined);
  }, [searchTerm, sortField, sortOrder]);

  const { data: newsData, isLoading: isLoadingNews } = useGetNewsQuery({
    search: searchTerm,
    sort: sortField,
    order: sortOrder,
    limit: pageSize,
    pageToken,
  });

  const { data: categoriesData } = useGetNewsCategoriesQuery();

  const [createNews, { isLoading: isCreatingNews }] = useCreateNewsMutation();
  const [updateNews, { isLoading: isUpdatingNews }] = useUpdateNewsMutation();
  const [deleteNews, { isLoading: isDeletingNews }] = useDeleteNewsMutation();
  const [publishNews, { isLoading: isPublishingNews }] =
    usePublishNewsMutation();
  const [uploadImage, { isLoading: isUploadingImage }] =
    useUploadNewsImageMutation();
  const [uploadVideo, { isLoading: isUploadingVideo }] =
    useUploadNewsVideoMutation();
  const [deleteImage] = useDeleteNewsImageMutation();
  const [deleteVideo] = useDeleteNewsVideoMutation();

  const [commentsAsList, setCommentsAsList] = useState(true);
  const { data: commentsData, isLoading: isLoadingComments } =
    useGetNewsCommentsQuery(
      {
        id: selectedNews?.id ?? 0,
        allAsList: commentsAsList,
      },
      { skip: !selectedNews }
    );
  const [deleteComment, { isLoading: isDeletingComment }] =
    useDeleteNewsCommentMutation();

  const me = useAppSelector(selectMe);
  const [sendPreviewEmail, { isLoading: isSendingPreviewEmail }] =
    useSendNewsPreviewEmailMutation();
  const [sendPreviewPush, { isLoading: isSendingPreviewPush }] =
    useSendNewsPreviewPushMutation();
  const [previewSnackbar, setPreviewSnackbar] = useState<{
    open: boolean;
    message: React.ReactNode;
  }>({ open: false, message: "" });

  // Handlers
  const handleSelectNews = (news: News) => {
    setSelectedNews(news);
    setTabValue(0);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenCreateDialog = () => {
    setNewsTitle("");
    setNewsText("");
    setNewsVideoDuration(0);
    setSelectedCategories([]);
    setOpenCreateDialog(true);
  };

  const handleOpenEditDialog = (news: News) => {
    setSelectedNews(news);
    setNewsTitle(news.title);
    setNewsText(news.text);
    setNewsVideoDuration(news.videoDurationSec);
    setSelectedCategories(news.categories.map((category) => category.id));
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (news: News) => {
    setSelectedNews(news);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenCreateDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
  };

  const handleSearch = () => {
    setPageToken(undefined);
  };

  const handleNextPage = () => {
    const next = newsData?.pageToken?.trim();
    if (next) {
      setPageToken(next);
    }
  };

  const handlePreviousPage = () => {
    setPageToken(undefined);
  };

  const handleSortChange = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
    setPageToken(undefined);
  };

  const isFormValid = () => {
    return newsTitle.trim() !== "" && newsText.trim() !== "";
  };

  const handleCreateNews = async () => {
    if (!isFormValid()) return;

    try {
      await createNews({
        title: newsTitle.trim(),
        text: newsText.trim(),
        videoDurationSec: newsVideoDuration,
        categories: selectedCategories,
      }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error creating news:", error);
    }
  };

  const handleUpdateNews = async () => {
    if (!selectedNews || !isFormValid()) return;

    try {
      await updateNews({
        id: selectedNews.id,
        body: {
          title: newsTitle.trim(),
          text: newsText.trim(),
          videoDurationSec: newsVideoDuration,
          categories: selectedCategories,
        },
      }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error updating news:", error);
    }
  };

  const handleDeleteNews = async () => {
    if (!selectedNews) return;

    try {
      await deleteNews(selectedNews.id).unwrap();
      handleCloseDialogs();
      setSelectedNews(null);
    } catch (error) {
      console.error("Error deleting news:", error);
    }
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!selectedNews || !event.target.files || event.target.files.length === 0)
      return;

    const file = event.target.files[0];
    const sizeError = getFileSizeLimitError(file, FILE_SIZE_LIMIT_BYTES.DEFAULT);
    if (sizeError) {
      alert(sizeError);
      if (imageFileRef.current) {
        imageFileRef.current.value = "";
      }
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await uploadImage({
        id: selectedNews.id,
        file: formData,
      }).unwrap();
      setSelectedNews({ ...selectedNews, imageUrl: res.url });
      if (imageFileRef.current) {
        imageFileRef.current.value = "";
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };

  const handleVideoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!selectedNews || !event.target.files || event.target.files.length === 0)
      return;

    const file = event.target.files[0];
    const sizeError = getFileSizeLimitError(
      file,
      FILE_SIZE_LIMIT_BYTES.CONTENT_AND_VIDEO,
    );
    if (sizeError) {
      alert(sizeError);
      if (videoFileRef.current) {
        videoFileRef.current.value = "";
      }
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await uploadVideo({
        id: selectedNews.id,
        file: formData,
      }).unwrap();

      setSelectedNews({ ...selectedNews, videoUrl: res.url });
      if (videoFileRef.current) {
        videoFileRef.current.value = "";
      }
    } catch (error) {
      console.error("Error uploading video:", error);
    }
  };

  const handleDeleteImage = async () => {
    if (!selectedNews) return;

    try {
      await deleteImage(selectedNews.id).unwrap();
      setSelectedNews({ ...selectedNews, imageUrl: "" });
    } catch (error) {
      console.error("Error deleting image:", error);
    }
  };

  const handleDeleteVideo = async () => {
    if (!selectedNews) return;

    try {
      await deleteVideo(selectedNews.id).unwrap();
      setSelectedNews({ ...selectedNews, videoUrl: "" });
    } catch (error) {
      console.error("Error deleting video:", error);
    }
  };

  const renderCommentItem = (comment: NewsComment, depth = 0) => (
    <ListItem
      alignItems="flex-start"
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        mb: 1,
        pl: 2 + depth * 2,
        bgcolor: "action.hover",
      }}
      secondaryAction={
        <IconButton
          edge="end"
          color="error"
          size="small"
          onClick={() => handleDeleteComment(comment.id)}
          disabled={isDeletingComment}
          title="Удалить комментарий"
        >
          <DeleteIcon />
        </IconButton>
      }
    >
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" color="primary">
              {getCommentAuthorName(comment)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {format(new Date(comment.createdAt), "dd.MM.yyyy HH:mm")}
            </Typography>
          </Stack>
        }
        secondary={
          <>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
            >
              {comment.text}
            </Typography>
            {comment.children?.length
              ? comment.children.map((child) => (
                  <Box key={child.id} sx={{ mt: 1 }}>
                    {renderCommentItem(child, depth + 1)}
                  </Box>
                ))
              : null}
          </>
        }
      />
    </ListItem>
  );

  const getCommentAuthorName = (comment: NewsComment): string => {
    const a = comment.author;
    if (!a) return "Пользователь";
    if (a.displayName) return a.displayName;
    if (a.firstName || a.lastName)
      return [a.lastName, a.firstName].filter(Boolean).join(" ").trim();
    return "Пользователь";
  };

  const handleSendPreviewEmail = async () => {
    if (!selectedNews) return;
    try {
      await sendPreviewEmail(selectedNews.id).unwrap();
      setPreviewSnackbar({
        open: true,
        message: me?.email ? (
          <>
            <Typography component="span" display="block">
              📧 Email отправлен на ваш адрес: {me.email}
            </Typography>
          </>
        ) : (
          <>
            <Typography component="span" display="block">
              📧 Email отправлен на ваш адрес в профиле
            </Typography>
          </>
        ),
      });
    } catch (error) {
      console.error("Error sending preview email:", error);
    }
  };

  const handleSendPreviewPush = async () => {
    if (!selectedNews) return;
    try {
      await sendPreviewPush(selectedNews.id).unwrap();
      setPreviewSnackbar({
        open: true,
        message: (
          <Typography component="span" display="block">
            📱 Push-уведомление должно прийти на ваше мобильное устройство
          </Typography>
        ),
      });
    } catch (error) {
      console.error("Error sending preview push:", error);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!selectedNews) return;
    try {
      await deleteComment({ id: selectedNews.id, commentId }).unwrap();
      if (commentsData?.comments) {
        setSelectedNews({
          ...selectedNews,
          commentCount: Math.max(0, selectedNews.commentCount - 1),
        });
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const handlePublishNews = async (news: News) => {
    try {
      const result = await publishNews(news.id).unwrap();
      if (result.result) {
        setSelectedNews({ ...news, status: "published" });
      }
    } catch (error) {
      console.error("Error publishing news:", error);
    }
  };

  // Render forms
  const renderNewsForm = () => (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Заголовок"
          fullWidth
          value={newsTitle}
          onChange={(e) => setNewsTitle(e.target.value)}
          margin="normal"
          required
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Текст новости"
          fullWidth
          multiline
          rows={6}
          value={newsText}
          onChange={(e) => setNewsText(e.target.value)}
          margin="normal"
          required
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Длительность видео (сек)"
          type="number"
          fullWidth
          value={newsVideoDuration}
          onChange={(e) => setNewsVideoDuration(parseInt(e.target.value) || 0)}
          margin="normal"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <VideocamIcon />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormControl fullWidth margin="normal">
          <InputLabel>Категории</InputLabel>
          <Select
            multiple
            value={selectedCategories}
            onChange={(e) => setSelectedCategories(e.target.value as number[])}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => {
                  const category = categoriesData?.categories.find(
                    (cat) => cat.id === value
                  );
                  return <Chip key={value} label={category?.name || value} />;
                })}
              </Box>
            )}
          >
            {categoriesData?.categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Выберите одну или несколько категорий</FormHelperText>
        </FormControl>
      </Grid>
    </Grid>
  );

  // Render dialogs
  const renderCreateDialog = () => (
    <Dialog
      open={openCreateDialog}
      onClose={handleCloseDialogs}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Добавить новость</DialogTitle>
      <DialogContent>{renderNewsForm()}</DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleCreateNews}
          disabled={!isFormValid() || isCreatingNews}
        >
          {isCreatingNews ? <CircularProgress size={24} /> : "Создать"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderEditDialog = () => (
    <Dialog
      open={openEditDialog}
      onClose={handleCloseDialogs}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Редактировать новость</DialogTitle>
      <DialogContent>{renderNewsForm()}</DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleUpdateNews}
          disabled={!isFormValid() || isUpdatingNews}
        >
          {isUpdatingNews ? <CircularProgress size={24} /> : "Сохранить"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Render news details
  const renderNewsDetails = () => {
    if (!selectedNews) return null;

    return (
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {selectedNews.title}
            </Typography>
            <Chip
              label={
                selectedNews.status === "published"
                  ? "Опубликовано"
                  : "Черновик"
              }
              color={
                selectedNews.status === "published" ? "success" : "default"
              }
              size="medium"
            />
          </Box>
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={1}
            alignItems="center"
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
          >
            {selectedNews.status === "draft" && (
              <Button
                variant="contained"
                color="success"
                size="medium"
                startIcon={<PublishIcon />}
                onClick={() => handlePublishNews(selectedNews)}
                disabled={isPublishingNews}
              >
                Опубликовать
              </Button>
            )}
            <Button
              variant="outlined"
              size="medium"
              startIcon={<EmailIcon />}
              onClick={handleSendPreviewEmail}
              disabled={isSendingPreviewEmail}
              title="Отправить тестовое письмо на ваш email"
            />
            <Button
              variant="outlined"
              size="medium"
              startIcon={<NotificationsActiveIcon />}
              onClick={handleSendPreviewPush}
              disabled={isSendingPreviewPush}
              title="Отправить тестовый push на ваше устройство"
            />
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => handleOpenEditDialog(selectedNews)}
            >
              Редактировать
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => handleOpenDeleteDialog(selectedNews)}
            >
              Удалить
            </Button>
          </Stack>
        </Box>

        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          gap={2}
          sx={{ mt: 2, mb: 2 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Дата публикации:
            </Typography>
            <Typography variant="body2">
              {format(new Date(selectedNews.createdAt), "dd.MM.yyyy HH:mm")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selectedNews.categories.map((category) => (
              <Chip key={category.id} label={category.name} size="small" />
            ))}
          </Box>
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="news tabs"
          >
            <Tab label="Содержание" />
            <Tab label="Медиа" />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CommentIcon fontSize="small" />
                  Комментарии ({selectedNews.commentCount})
                </Box>
              }
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="body1" paragraph>
            {selectedNews.text}
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Статистика
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Typography variant="body2">
                Комментарии: {selectedNews.commentCount}
              </Typography>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={commentsAsList}
                  onChange={(e) => setCommentsAsList(e.target.checked)}
                  color="primary"
                />
              }
              label="Показать списком (последние сверху)"
            />
          </Box>
          {isLoadingComments ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : commentsData?.comments?.length ? (
            <List disablePadding>
              {commentsAsList
                ? commentsData.comments.map((comment) => (
                    <ListItem
                      key={comment.id}
                      alignItems="flex-start"
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: "action.hover",
                      }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          color="error"
                          size="small"
                          onClick={() => handleDeleteComment(comment.id)}
                          disabled={isDeletingComment}
                          title="Удалить комментарий"
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle2" color="primary">
                              {getCommentAuthorName(comment)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(
                                new Date(comment.createdAt),
                                "dd.MM.yyyy HH:mm"
                              )}
                            </Typography>
                          </Stack>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                          >
                            {comment.text}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))
                : commentsData.comments.map((comment) => (
                    <Box key={comment.id} sx={{ mb: 1 }}>
                      {renderCommentItem(comment)}
                    </Box>
                  ))}
            </List>
          ) : (
            <Typography color="text.secondary">
              Комментариев пока нет.
            </Typography>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <PhotoIcon sx={{ mr: 1 }} /> Изображение
                  </Typography>

                  {selectedNews.imageUrl ? (
                    <>
                      <CardMedia
                        component="img"
                        height="200"
                        image={selectedNews.imageUrl}
                        alt={selectedNews.title}
                        sx={{ objectFit: "contain", mb: 2 }}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleDeleteImage}
                      >
                        Удалить
                      </Button>
                    </>
                  ) : (
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Нет изображения
                      </Typography>
                      <input
                        ref={imageFileRef}
                        type="file"
                        accept="image/jpeg,image/png"
                        style={{ display: "none" }}
                        onChange={handleImageUpload}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => imageFileRef.current?.click()}
                        disabled={isUploadingImage}
                      >
                        {isUploadingImage ? (
                          <CircularProgress size={24} />
                        ) : (
                          "Загрузить изображение"
                        )}
                      </Button>
                      <FileUploadInfo
                        type="image"
                        recommendedSize="1200x800 пикселей"
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <VideocamIcon sx={{ mr: 1 }} /> Видео
                  </Typography>

                  {selectedNews.videoUrl ? (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <video
                          controls
                          width="100%"
                          height="200"
                          src={selectedNews.videoUrl}
                        />
                      </Box>
                      {/* <Typography variant="body2" gutterBottom>
                        Длительность: {selectedNews.videoDurationSec} сек
                      </Typography> */}
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleDeleteVideo}
                      >
                        Удалить
                      </Button>
                    </>
                  ) : (
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Нет видео
                      </Typography>
                      <input
                        ref={videoFileRef}
                        type="file"
                        accept="video/mp4,video/webm,video/quicktime"
                        style={{ display: "none" }}
                        onChange={handleVideoUpload}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => videoFileRef.current?.click()}
                        disabled={isUploadingVideo}
                      >
                        {isUploadingVideo ? (
                          <CircularProgress size={24} />
                        ) : (
                          "Загрузить видео"
                        )}
                      </Button>
                      <FileUploadInfo
                        type="video"
                        recommendedSize="1920x1080 пикселей"
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight={600} fontSize={24} mb={2}>
        Управление новостями
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Поиск новостей"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSearch}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Сортировка</InputLabel>
              <Select
                value={sortField}
                label="Сортировка"
                onChange={(e) => handleSortChange(String(e.target.value))}
              >
                <MenuItem value="title">Заголовок</MenuItem>
                <MenuItem value="createdAt">Дата создания</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
              fullWidth
            >
              Добавить новость
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          mt: 3,
          gap: 2,
          minHeight: 0,
        }}
      >
        <Paper
          sx={{
            width: { xs: "100%", md: 350 },
            minWidth: { md: 350 },
            flexShrink: 0,
            p: 2,
          }}
        >
          <Typography gutterBottom fontWeight={600} fontSize={18}>
            Список новостей
          </Typography>

          {isLoadingNews ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : newsData?.news?.length ? (
            <>
              <List>
                {newsData.news.map((news) => (
                  <ListItem
                    key={news.id}
                    sx={{
                      borderRadius: 1,
                      backgroundColor:
                        selectedNews?.id === news.id ? "#515151" : "inherit",
                      "&:hover": {
                        cursor: "pointer",
                        backgroundColor: "#414141",
                      },
                      mb: 1,
                    }}
                    onClick={() => handleSelectNews(news)}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDeleteDialog(news);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {news.title}
                          <Chip
                            label={
                              news.status === "published"
                                ? "Опубликовано"
                                : "Черновик"
                            }
                            color={
                              news.status === "published"
                                ? "success"
                                : "default"
                            }
                            size="small"
                          />
                        </Box>
                      }
                      secondary={format(new Date(news.createdAt), "dd.MM.yyyy")}
                    />
                  </ListItem>
                ))}
              </List>

              {newsData.news.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    disabled={!pageToken}
                    onClick={handlePreviousPage}
                  >
                    Предыдущая
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={!newsData.pageToken?.trim()}
                    onClick={handleNextPage}
                  >
                    Следующая
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Typography color="text.secondary" align="center" py={3}>
              Новости не найдены
            </Typography>
          )}
        </Paper>

        <Paper sx={{ flex: 1, minWidth: 0, p: 2 }}>
          {selectedNews ? (
            renderNewsDetails()
          ) : (
            <Typography color="text.secondary" align="center" py={3}>
              Выберите новость для просмотра деталей
            </Typography>
          )}
        </Paper>
      </Box>

      {renderCreateDialog()}
      {renderEditDialog()}
      {openDeleteDialog && selectedNews && (
        <Dialog open={openDeleteDialog} onClose={handleCloseDialogs}>
          <DialogTitle>Удалить новость</DialogTitle>
          <DialogContent>
            <Typography>
              Вы уверены, что хотите удалить новость "{selectedNews.title}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>Отмена</Button>
            <Button
              color="error"
              onClick={handleDeleteNews}
              disabled={isDeletingNews}
            >
              {isDeletingNews ? <CircularProgress size={24} /> : "Удалить"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={previewSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setPreviewSnackbar((p) => ({ ...p, open: false }))}
        message={previewSnackbar.message}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        ContentProps={{ sx: { whiteSpace: "pre-line" } }}
      />
    </Box>
  );
};
